import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Check, Paintbrush } from 'lucide-react';
import { BrandingEditor } from '@/components/branding/BrandingEditor';
import { GenieAvatar } from '@/components/GenieAvatar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAccountBranding } from '@/hooks/branding/useAccountBranding';
import { useAuth } from '@/auth/AuthProvider';
import { getRandomGenieDefaultImage } from '@/data/genie/defaults';
import { DEFAULT_BRANDING, GenieBranding } from '@/types/ui/branding';
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

// Static icon for the page header (consistent with System & Settings section)

const AccountBrandingPage = () => {
  const { team } = useAuth();
  const { branding, isLoading, hasAccountBranding, updateBranding, isUpdating } = useAccountBranding();

  // Local state for editing
  const [localBranding, setLocalBranding] = useState<GenieBranding>(DEFAULT_BRANDING);

  // Random default image for preview (stable during session)
  const defaultPreviewImage = useMemo(() => getRandomGenieDefaultImage(), []);

  // Sync local state with fetched branding
  useEffect(() => {
    if (branding) {
      setLocalBranding(branding);
    }
  }, [branding]);

  const handleSave = () => {
    updateBranding(localBranding);
  };

  if (!team) {
    return (
      <div className="min-h-screen bg-[#F9FAFC]">
        <div className="container mx-auto px-4 py-6 md:py-8 max-w-[1920px]">
          <div className="flex items-center mb-6 md:mb-8">
            <GenieIcon name="palette" size={32} className="h-7 w-7 md:h-8 md:w-8 mr-3 md:mr-4" />
            <h1 className="text-xl md:text-3xl font-bold text-[#222B45]">Account Branding</h1>
          </div>
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-gray-600">
                You need to create a team to set up account branding.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                <Link to="/genie-hub/account-settings" className="text-genie-primary font-medium">
                  Go to Account Settings
                </Link>{' '}
                to create your team.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <div className="container mx-auto px-4 py-6 md:py-8 max-w-[1920px]">
        <div className="w-full">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-0 mb-6 md:mb-8">
            <div className="flex items-center">
              <GenieIcon name="palette" size={32} className="h-7 w-7 md:h-8 md:w-8 mr-3 md:mr-4" />
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-xl md:text-3xl font-bold text-[#222B45] leading-tight">
                    Account Branding
                  </h1>
                  {hasAccountBranding && (
                    <div
                      className="px-3 py-1 rounded-full text-xs font-semibold shadow-sm border-2 whitespace-nowrap flex items-center gap-1"
                      style={{
                        background: 'linear-gradient(135deg, #E8F4FF 0%, #FFE8F5 100%)',
                        borderColor: '#4E9CFF',
                        color: '#4E9CFF',
                      }}
                    >
                      <Check className="w-3 h-3" />
                      Active
                    </div>
                  )}
                </div>
                <p className="text-sm md:text-base text-[#64748B] mt-1 hidden sm:block">
                  Set default branding for all new Genies
                </p>
              </div>
            </div>
          </div>

          {/* Info Banner */}
          <div
            className="mb-6 rounded-lg px-4 py-3"
            style={{
              background: 'linear-gradient(white, white) padding-box, linear-gradient(90deg, #4E9CFF, #FF4DB8, #4E9CFF) border-box',
              backgroundSize: '200% 200%',
              border: '2px solid transparent',
            }}
          >
            <p className="font-medium text-gray-900">
              How Account Branding Works
            </p>
            <p className="text-sm text-gray-600 mt-1">
              When you set up account branding, all <strong>newly created Genies</strong> will automatically use these branding settings as their default.
              Existing Genies keep their current branding and won&apos;t be affected.
            </p>
          </div>

          {/* Main Content - Two Column Layout */}
          <div className="flex gap-6 flex-wrap">
            {/* Left Column - Editor */}
            <div className="flex-1 min-w-[400px]">
              <Card>
                <CardHeader>
                  <CardTitle>Branding Settings</CardTitle>
                  <CardDescription>
                    Configure your default logo, colors, and styling
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-genie-primary" />
                    </div>
                  ) : (
                    <BrandingEditor
                      branding={localBranding}
                      onChange={setLocalBranding}
                      onSave={handleSave}
                      isSaving={isUpdating}
                      allowLogoDelete={true}
                      genieName={team?.name || undefined}
                    />
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Preview */}
            <div className="w-full lg:w-[380px] space-y-6">
              {/* Genie Preview */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Preview</CardTitle>
                  <CardDescription>
                    How new Genies will look by default
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-gray-50 rounded-lg p-6 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-32 h-32 mx-auto">
                        <GenieAvatar
                          color={localBranding.primaryColor}
                          secondaryColor={localBranding.secondaryColor}
                          gradientEnabled={localBranding.gradientEnabled}
                          gradientAngle={localBranding.gradientAngle}
                          avatarBackgroundOpacity={localBranding.avatarBackgroundOpacity}
                          customAvatarUrl={localBranding.logoUrl || localBranding.customAvatarUrl || defaultPreviewImage}
                          hasCustomBranding={true}
                          size={128}
                        />
                      </div>
                      <p className="text-sm text-gray-500 mt-4">
                        {localBranding.logoUrl || localBranding.customAvatarUrl
                          ? 'New Genie Avatar'
                          : 'Sample (random default image)'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Color Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Active Colors</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Primary</span>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded-full border border-gray-200"
                          style={{ backgroundColor: localBranding.primaryColor }}
                        />
                        <span className="text-sm font-mono text-gray-700">
                          {localBranding.primaryColor}
                        </span>
                      </div>
                    </div>

                    {localBranding.gradientEnabled && localBranding.secondaryColor && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Secondary</span>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-6 h-6 rounded-full border border-gray-200"
                            style={{ backgroundColor: localBranding.secondaryColor }}
                          />
                          <span className="text-sm font-mono text-gray-700">
                            {localBranding.secondaryColor}
                          </span>
                        </div>
                      </div>
                    )}

                    {localBranding.logoUrl && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Logo</span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#4E9CFF]/10 text-[#4E9CFF] border border-[#4E9CFF]/30">
                          <Check className="w-3 h-3" />
                          Uploaded
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Status Card */}
              <Card className={hasAccountBranding ? 'border-[#4E9CFF]/30 bg-[#4E9CFF]/5' : 'border-gray-200'}>
                <CardContent className="py-4">
                  <div className="text-center">
                    {hasAccountBranding ? (
                      <>
                        <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-[#4E9CFF]/10 flex items-center justify-center">
                          <Check className="w-6 h-6 text-[#4E9CFF]" />
                        </div>
                        <p className="font-medium text-[#4E9CFF]">Account Branding Active</p>
                        <p className="text-sm text-[#4E9CFF]/70 mt-1">
                          New Genies will use these settings
                        </p>
                        {branding?.lastUpdatedAt && (
                          <p className="text-xs text-gray-500 mt-2">
                            Last updated {new Date(branding.lastUpdatedAt).toLocaleDateString()}
                            {branding.lastUpdatedByName && ` by ${branding.lastUpdatedByName}`}
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-gray-100 flex items-center justify-center">
                          <Paintbrush className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="font-medium text-gray-700">No Account Branding Set</p>
                        <p className="text-sm text-gray-500 mt-1">
                          Configure and save to activate
                        </p>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountBrandingPage;
